import {
  Box,
  Button,
  Icon,
  SearchSelect,
  Stack,
  Text,
  ToolbarButtonWrapper,
  type SearchSelectProps,
  type SelectItem,
} from '@talos/kyoko';
import type { ComponentType } from 'react';
import styled from 'styled-components';

export const MobileNavMenuButton = styled(ToolbarButtonWrapper).attrs({ as: 'button', type: 'button' })<{
  $isOrgMorphed?: boolean;
}>`
  border: none;
  color: ${({ theme }) => theme.colorTextImportant};
  cursor: pointer;
  flex: 0 1 auto;
  gap: ${({ theme }) => theme.spacing12}px;
  max-width: 100%;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing12}px`};
  background: ${({ background }) => background};

  & > :first-child,
  & > :last-child {
    flex: 0 0 auto;
  }
`;

export const MobileNavMenuLabel = styled(Text)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ theme }) => theme.fontWeightBold};
`;

export const SheetWrapper = styled(Stack)`
  height: 100%;
  min-height: 0;
`;

export const SheetHeader = styled(Stack)`
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing12}px;
  padding-top: ${({ theme }) => theme.spacing24}px;
`;

export const SheetNavItems = styled(Box)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing16}px;
`;

export const SheetFooter = styled(Box)`
  flex: 0 0 auto;
  padding: ${({ theme }) => theme.spacing16}px;
  border-top: 1px solid ${({ theme }) => theme.backgroundDivider};

  & > * {
    width: 100%;
    min-width: 0;
  }
`;

export const MobileUserFooter = styled(Stack)`
  gap: ${({ theme }) => theme.spacing8}px;
`;

export const MobileFooterTop = styled(Stack)`
  gap: ${({ theme }) => theme.spacing8}px;
`;

export const MobileUserSummary = styled(Stack)`
  background: ${({ theme }) => theme.backgroundCard};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  gap: ${({ theme }) => theme.spacing6}px;
  padding: ${({ theme }) => `${theme.spacing8}px ${theme.spacing12}px`};
`;

export const MobileUserNameRow = styled(Box)`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing8}px;
  min-width: 0;
`;

export const MobileVersionText = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizeXs}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  line-height: ${({ theme }) => theme.lineHeightXs}rem;
`;

export const MobileThemeRow = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colorTextDefault};
  display: flex;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing12}px`};
`;

export const MobileOrganizationRow = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colorTextDefault};
  display: flex;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing12}px`};
`;

export const MobileOrganizationSelect = styled(SearchSelect as ComponentType<SearchSelectProps<SelectItem>>)`
  flex: 1;
  min-width: 0;
  background: ${({ theme }) => theme.colors.gray['010']};
`;

export const MobileFooterButton = styled(Button)`
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing12}px;
  width: 100%;
`;

const BasePickerRow = styled(Button)<{ isActive?: boolean }>`
  align-items: center;
  background: ${({ isActive, theme }) => (isActive ? theme.backgroundSidebarItemActive : undefined)};
  border-radius: ${({ theme }) => theme.spacing8}px;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => theme.spacing8}px;
  justify-content: flex-start;
  text-align: left;
  width: auto;

  ${Icon}:last-child {
    margin-left: auto;
    opacity: 0.5;
  }
`;

export const CurrentSectionButton = styled(BasePickerRow)`
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  align-self: center;
`;

export const CurrentSectionLabel = styled(Text)`
  font-weight: ${({ theme }) => theme.fontWeightBold};
`;

export const PickerRow = styled(BasePickerRow)`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  padding: ${({ theme }) => theme.spacing12}px;
  font-weight: normal;
`;

export const GroupLabel = styled(Box)`
  color: ${({ theme }) => theme.colorTextSubtle};
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  font-weight: ${({ theme }) => theme.fontWeightMedium};
  padding: ${({ theme }) => `${theme.spacing8}px ${theme.spacing4}px ${theme.spacing4}px`};
  text-transform: uppercase;
`;
